// Shared client/server helpers for therapist licensing & jurisdictional
// visibility. Used by both the directory filter UI and (via re-export) the
// therapist application form.

import type { Therapist } from "@/lib/therapists-data";

export type AdditionalJurisdiction = {
  country: string; // ISO-ish code: GB, US, IE, CA, AU, ...
  states?: string[]; // 2-letter US state codes when country = US
  regulatory_body?: string;
  licence_number?: string;
  licence_expiry?: string; // YYYY-MM-DD
};

export type LicensingProfile = {
  licensed_country: string; // ISO-ish code: GB, US, IE, CA, AU
  licensed_states: string[]; // US state codes (or UK nations)
  licence_type?: string;
  licence_number?: string;
  licence_expiry?: string;
  regulatory_body?: string;
  telehealth_permitted: boolean;
  additional_jurisdictions: AdditionalJurisdiction[];
};

// ---- Country & state normalisation -----------------------------------------

const COUNTRY_ALIASES: Record<string, string> = {
  "united kingdom": "GB",
  uk: "GB",
  britain: "GB",
  "great britain": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  "northern ireland": "GB",
  gb: "GB",
  "united states": "US",
  "united states of america": "US",
  usa: "US",
  "u.s.": "US",
  "u.s.a.": "US",
  america: "US",
  us: "US",
  ireland: "IE",
  ie: "IE",
  canada: "CA",
  ca: "CA",
  australia: "AU",
  au: "AU",
  germany: "DE",
  de: "DE",
  france: "FR",
  fr: "FR",
  spain: "ES",
  es: "ES",
  portugal: "PT",
  pt: "PT",
  netherlands: "NL",
  nl: "NL",
};

// ---- Full country list (ISO 3166-1 alpha-2) --------------------------------
// Sorted alphabetically by display name. Used by the therapist application form
// combobox so practitioners from any country can apply.
export const COUNTRY_OPTIONS: { code: string; name: string }[] = [
  { code: "AF", name: "Afghanistan" }, { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" }, { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" }, { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" }, { code: "AM", name: "Armenia" },
  { code: "AU", name: "Australia" }, { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" }, { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" }, { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" }, { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" }, { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" }, { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" }, { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" }, { code: "BR", name: "Brazil" },
  { code: "BN", name: "Brunei" }, { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" }, { code: "BI", name: "Burundi" },
  { code: "KH", name: "Cambodia" }, { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" }, { code: "CV", name: "Cape Verde" },
  { code: "CF", name: "Central African Republic" }, { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" }, { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" }, { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" }, { code: "CD", name: "Congo (DRC)" },
  { code: "CR", name: "Costa Rica" }, { code: "CI", name: "Côte d'Ivoire" },
  { code: "HR", name: "Croatia" }, { code: "CU", name: "Cuba" },
  { code: "CY", name: "Cyprus" }, { code: "CZ", name: "Czech Republic" },
  { code: "DK", name: "Denmark" }, { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" }, { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" }, { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" }, { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" }, { code: "EE", name: "Estonia" },
  { code: "SZ", name: "Eswatini" }, { code: "ET", name: "Ethiopia" },
  { code: "FJ", name: "Fiji" }, { code: "FI", name: "Finland" },
  { code: "FR", name: "France" }, { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" }, { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" }, { code: "GH", name: "Ghana" },
  { code: "GR", name: "Greece" }, { code: "GD", name: "Grenada" },
  { code: "GT", name: "Guatemala" }, { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" }, { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" }, { code: "HN", name: "Honduras" },
  { code: "HK", name: "Hong Kong" }, { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" }, { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" }, { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" }, { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" }, { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" }, { code: "JP", name: "Japan" },
  { code: "JO", name: "Jordan" }, { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" }, { code: "KI", name: "Kiribati" },
  { code: "XK", name: "Kosovo" }, { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" }, { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" }, { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" }, { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" }, { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" }, { code: "LU", name: "Luxembourg" },
  { code: "MO", name: "Macau" }, { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" }, { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" }, { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" }, { code: "MH", name: "Marshall Islands" },
  { code: "MR", name: "Mauritania" }, { code: "MU", name: "Mauritius" },
  { code: "MX", name: "Mexico" }, { code: "FM", name: "Micronesia" },
  { code: "MD", name: "Moldova" }, { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" }, { code: "ME", name: "Montenegro" },
  { code: "MA", name: "Morocco" }, { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" }, { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" }, { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" }, { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" }, { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" }, { code: "MK", name: "North Macedonia" },
  { code: "NO", name: "Norway" }, { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" }, { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine" }, { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" }, { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" }, { code: "PH", name: "Philippines" },
  { code: "PL", name: "Poland" }, { code: "PT", name: "Portugal" },
  { code: "PR", name: "Puerto Rico" }, { code: "QA", name: "Qatar" },
  { code: "RO", name: "Romania" }, { code: "RU", name: "Russia" },
  { code: "RW", name: "Rwanda" }, { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" }, { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" }, { code: "SM", name: "San Marino" },
  { code: "ST", name: "São Tomé and Príncipe" }, { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" }, { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" }, { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" }, { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" }, { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" }, { code: "ZA", name: "South Africa" },
  { code: "KR", name: "South Korea" }, { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" }, { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" }, { code: "SR", name: "Suriname" },
  { code: "SE", name: "Sweden" }, { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" }, { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" }, { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" }, { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" }, { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" }, { code: "TN", name: "Tunisia" },
  { code: "TR", name: "Turkey" }, { code: "TM", name: "Turkmenistan" },
  { code: "TV", name: "Tuvalu" }, { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" }, { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" }, { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" }, { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" }, { code: "VA", name: "Vatican City" },
  { code: "VE", name: "Venezuela" }, { code: "VN", name: "Vietnam" },
  { code: "YE", name: "Yemen" }, { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
].sort((a, b) => a.name.localeCompare(b.name));

const COUNTRY_NAME_TO_CODE: Record<string, string> = COUNTRY_OPTIONS.reduce(
  (acc, c) => {
    acc[c.name.toLowerCase()] = c.code;
    return acc;
  },
  {} as Record<string, string>,
);
const COUNTRY_CODE_TO_NAME: Record<string, string> = COUNTRY_OPTIONS.reduce(
  (acc, c) => {
    acc[c.code] = c.name;
    return acc;
  },
  {} as Record<string, string>,
);

/** Resolve a country name (case-insensitive) or ISO code to an ISO code, or "" if unknown. */
export function countryCodeFromName(input?: string | null): string {
  if (!input) return "";
  const trimmed = input.trim();
  if (!trimmed) return "";
  const upper = trimmed.toUpperCase();
  if (COUNTRY_CODE_TO_NAME[upper]) return upper;
  const alias = COUNTRY_ALIASES[trimmed.toLowerCase()];
  if (alias) return alias;
  return COUNTRY_NAME_TO_CODE[trimmed.toLowerCase()] ?? "";
}

/** Resolve an ISO code to its display name, or return the input unchanged. */
export function countryNameFromCode(code?: string | null): string {
  if (!code) return "";
  return COUNTRY_CODE_TO_NAME[code.trim().toUpperCase()] ?? code;
}

export function normaliseCountry(input?: string | null): string {
  if (!input) return "";
  const k = input.trim().toLowerCase();
  if (COUNTRY_ALIASES[k]) return COUNTRY_ALIASES[k];
  if (COUNTRY_NAME_TO_CODE[k]) return COUNTRY_NAME_TO_CODE[k];
  const upper = input.trim().toUpperCase();
  if (COUNTRY_CODE_TO_NAME[upper]) return upper;
  return upper.slice(0, 2);
}

const US_STATES: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM",
  "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA",
  "rhode island": "RI", "south carolina": "SC", "south dakota": "SD",
  tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT", virginia: "VA",
  washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC",
};

export const US_STATE_OPTIONS = Object.entries(US_STATES)
  .map(([name, code]) => ({ code, name: name.replace(/\b\w/g, (c) => c.toUpperCase()) }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function normaliseState(input?: string | null, country?: string): string {
  if (!input) return "";
  const trimmed = input.trim();
  if (normaliseCountry(country) !== "US") return trimmed; // free-form for non-US
  const upper = trimmed.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper) && Object.values(US_STATES).includes(upper)) return upper;
  const lower = trimmed.toLowerCase();
  return US_STATES[lower] ?? upper.slice(0, 2);
}

// Derive a licensing profile from a static Therapist entry that doesn't yet
// carry explicit licensing fields. Looks at country + licensingJurisdiction.
export function deriveLicensingFromTherapist(t: Therapist): LicensingProfile {
  const licensed_country = normaliseCountry(t.country);
  const states: string[] = [];
  if (licensed_country === "US") {
    if (t.licensedStates && t.licensedStates.length > 0) {
      for (const s of t.licensedStates) {
        const code = normaliseState(s, "US");
        if (code && !states.includes(code)) states.push(code);
      }
    } else if (t.licensingJurisdiction) {
      // Parse "California, USA" / "California & Florida, USA" / "CA; FL"
      const raw = t.licensingJurisdiction.replace(/,?\s*USA?\s*$/i, "");
      for (const part of raw.split(/[,;&/]| and /i)) {
        const code = normaliseState(part.trim(), "US");
        if (code && !states.includes(code)) states.push(code);
      }
    }
  }
  return {
    licensed_country,
    licensed_states: states,
    regulatory_body: t.professionalRegistrations?.[0]?.body,
    licence_number: t.professionalRegistrations?.[0]?.number,
    telehealth_permitted: false,
    additional_jurisdictions: [],
  };
}

// ---- Visibility rule -------------------------------------------------------

export type UserLocation = { country: string; region_state: string };

export function isTherapistVisibleToUser(
  licensing: LicensingProfile,
  user: UserLocation,
): boolean {
  const uc = normaliseCountry(user.country);
  if (!uc) return false;
  const us = (user.region_state || "").toUpperCase();

  const matchesJurisdiction = (country: string, states: string[]) => {
    const jc = normaliseCountry(country);
    if (jc !== uc) return false;
    if (uc === "US") {
      if (!us) return false;
      const upper = states.map((s) => s.toUpperCase());
      return upper.includes(us);
    }
    return true;
  };

  if (matchesJurisdiction(licensing.licensed_country, licensing.licensed_states)) {
    return true;
  }
  for (const j of licensing.additional_jurisdictions ?? []) {
    if (matchesJurisdiction(j.country, j.states ?? [])) return true;
  }
  return false;
}

// Decide which empty-state message to show.
export function emptyStateMessageFor(user: UserLocation): {
  title: string;
  body: string;
} {
  const uc = normaliseCountry(user.country);
  if (uc === "GB") {
    return {
      title: "No therapists available for your location yet",
      body:
        "Therapists are licensed by country and region. US therapists are not available to UK-based users unless they are legally licensed to practise in the UK.",
    };
  }
  return {
    title: "No therapists available for your location yet",
    body:
      "We don't yet have therapists in our directory who are legally permitted to practise in your country or state. Please check back soon.",
  };
}
